import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClickOutside } from '../../hooks/useClickOutside';
import { NAME_SEARCH_DEBOUNCE_MS } from '../../constants/app.constants';
import { OrderContext } from '../../contexts/OrderContext';
import type { ShipstationOrder } from '../../types/apiTypes';
import type { OrderDetailNavState } from './orderdetail/orderDetailNavState';
import {
  formatOrderSearchLabel,
  getOrderStatusFlairs,
  matchOrdersForSearch,
} from './orderSearchUtils';
import '../labels/PrintBoxLabel.css';
import './OrdersList.css';
import './OrderSearchModal.css';

interface OrderSearchModalProps {
  open: boolean;
  orders: readonly ShipstationOrder[];
  tagDictionary: Record<string, string | number>;
  navState: OrderDetailNavState;
  onClose: () => void;
}

function OrderSearchModal({
  open,
  orders,
  tagDictionary,
  navState,
  onClose,
}: OrderSearchModalProps): React.ReactElement | null {
  const navigate = useNavigate();
  const context = useContext(OrderContext);
  const {
    setSelectedOrderId,
    setOrderItems,
    setShipToName,
  } = context!;

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ShipstationOrder[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [searchSettled, setSearchSettled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
    setShowAutocomplete(false);
    setSelectedIndex(-1);
    setSearchSettled(false);
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setShowAutocomplete(false);
      setSelectedIndex(-1);
      setSearchSettled(false);
      return undefined;
    }

    setSearchSettled(false);
    const timer = window.setTimeout(() => {
      const matches = matchOrdersForSearch(orders, trimmed);
      setResults(matches);
      setShowAutocomplete(matches.length > 0);
      setSelectedIndex(-1);
      setSearchSettled(true);
    }, NAME_SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query, orders, open]);

  useEffect(() => {
    if (showAutocomplete && inputRef.current && autocompleteRef.current) {
      const inputRect = inputRef.current.getBoundingClientRect();
      const dropdown = autocompleteRef.current;
      dropdown.style.top = `${inputRect.bottom}px`;
      dropdown.style.left = `${inputRect.left}px`;
      dropdown.style.width = `${inputRect.width}px`;
    }
  }, [showAutocomplete, query, results]);

  useClickOutside(
    [autocompleteRef, inputRef],
    () => setShowAutocomplete(false),
  );

  if (!open) {
    return null;
  }

  const selectOrder = (order: ShipstationOrder): void => {
    setSelectedOrderId(order.orderId);
    setOrderItems([]);
    setShipToName('');
    onClose();
    navigate(`/order/${order.orderId}`, { state: navState });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      if (showAutocomplete) {
        setShowAutocomplete(false);
        return;
      }
      onClose();
      return;
    }
    if (!showAutocomplete || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      selectOrder(results[selectedIndex]);
    }
  };

  return (
    <div className="pbl-modal-overlay" onClick={onClose}>
      <div className="pbl-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pbl-modal-close" onClick={onClose}>
          ×
        </button>
        <div className="pbl-modal-title">Enter order number or customer name</div>
        <div className="pbl-manual-popup">
          <div className="pbl-sku-input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Order number or customer name"
              className="pbl-sku-input"
              autoComplete="off"
            />
            {showAutocomplete && results.length > 0 && (
              <div ref={autocompleteRef} className="pbl-autocomplete-dropdown">
                {results.map((order, index) => {
                  const { holdOrder, clearToFulfill } = getOrderStatusFlairs(order, tagDictionary);
                  return (
                    <div
                      key={String(order.orderId)}
                      className={`pbl-autocomplete-item order-search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                      onClick={() => selectOrder(order)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      <span className="order-search-result-label">
                        {formatOrderSearchLabel(order)}
                      </span>
                      <div className="order-flair-container">
                        <div
                          className="order-box-flair"
                          style={{ backgroundColor: holdOrder ? '#FF0000' : 'transparent' }}
                        />
                        <div
                          className="order-box-flair"
                          style={{ backgroundColor: clearToFulfill ? '#1eb01e' : 'transparent' }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {searchSettled && query.trim() && results.length === 0 && (
            <div className="order-search-status">No matching orders</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OrderSearchModal;
